import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as WalletIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { TransactionService, Transaction } from '../../services/transactionService';
import { supabase } from '../../lib/supabase';

interface TransactionDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  orderId: string; // Purchase order ID
}

const TransactionDetailsPopup: React.FC<TransactionDetailsPopupProps> = ({
  open,
  onClose,
  orderId,
}) => {
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && orderId) {
      loadTransaction();
    } else {
      setTransaction(null);
      setError(null);
    }
  }, [open, orderId]);

  const loadTransaction = async () => {
    setLoading(true);
    setError(null);
    try {
      // First try to resolve the purchase -> transaction link
      const { data: purchaseRow, error: purchaseErr } = await supabase
        .from('purchases')
        .select('id, transaction_id')
        .eq('id', orderId)
        .maybeSingle();

      if (purchaseErr) throw purchaseErr;

      const txId = (purchaseRow as any)?.transaction_id as string | null | undefined;
      if (txId) {
        const tx = await TransactionService.getTransaction(txId);
        if (tx) {
          setTransaction(tx);
          return;
        }
      }

      // Legacy fallback: older flows stored purchase id in transactions.order_id
      const transactions = await TransactionService.getTransactionsByOrderId(orderId);
      if (transactions && transactions.length > 0) {
        const completedTransaction = transactions.find((t) => t.status === 'completed');
        setTransaction(completedTransaction || transactions[0]);
      } else {
        setError('Transaction not found for this order');
      }
    } catch (err: any) {
      console.error('Error loading transaction:', err);
      setError(err.message || 'Failed to load transaction details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'cancelled':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <DialogTitle sx={{ position: 'relative', pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ReceiptIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Transaction Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete payment information
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : transaction ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Status Badge */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={transaction.status.toUpperCase()}
                color={getStatusColor(transaction.status) as any}
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  px: 2,
                  py: 2.5,
                  '& .MuiChip-icon': {
                    fontSize: '1.2rem',
                  },
                }}
              />
            </Box>

            {/* Amount Section */}
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                border: '1px solid rgba(76, 175, 80, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <MoneyIcon sx={{ color: '#4CAF50', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Payment Amount
                </Typography>
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  color: '#4CAF50',
                  textAlign: 'center',
                  mb: 2,
                }}
              >
                ${transaction.total_amount.toFixed(2)} {transaction.currency || 'USD'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  pt: 2,
                  borderTop: '1px solid rgba(76, 175, 80, 0.2)',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Vendor Amount
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    ${transaction.vendor_amount.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Platform Fee
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    ${transaction.platform_amount.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Transaction Information */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                Transaction Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ReceiptIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Transaction ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                      {transaction.id}
                    </Typography>
                  </Box>
                </Box>

                {transaction.paypal_order_id && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PaymentIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        PayPal Order ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {transaction.paypal_order_id}
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CalendarIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Date & Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDate(transaction.created_at)}
                    </Typography>
                  </Box>
                </Box>

                {transaction.completed_at && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Completed At
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(transaction.completed_at)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider />

            {/* Payment Recipients */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                Payment Recipients
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(0, 112, 186, 0.05)',
                    border: '1px solid rgba(0, 112, 186, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <WalletIcon sx={{ color: '#0070BA', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Vendor Email
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {transaction.vendor_email}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(76, 175, 80, 0.05)',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <WalletIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Platform Email
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {transaction.platform_email}
                  </Typography>
                </Box>

                {transaction.payer_email && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: 'rgba(102, 126, 234, 0.05)',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <EmailIcon sx={{ color: '#667eea', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Payer Email
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                      {transaction.payer_email}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #45a049 0%, #388e3c 100%)',
            },
            textTransform: 'none',
            borderRadius: 2,
            fontWeight: 600,
            px: 4,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDetailsPopup;

